/**
 * Admin · Code Maintenance
 * ─────────────────────────
 * Lists refactor phase reports (assessment + evidence) from
 * /api/admin/refactor-reports and lets an admin read the raw markdown
 * of each report in a right-side pane.
 *
 * Design: intentionally minimal — no fancy toolbar. Left rail lists
 * available reports grouped by phase (A, B, C, D). Right pane renders
 * the selected report as pre-wrap plain text (markdown source), which
 * keeps the report as the single source of truth without pulling a
 * heavy markdown renderer for one admin page. Table blocks in the
 * markdown will render as monospaced ASCII — legible enough for an
 * admin sign-off surface.
 *
 * Mounted at /admin/code-maintenance. Not linked from anywhere yet —
 * follows the same admin-URL-typed pattern as /admin/dashboard.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, RefreshCcw, ShieldCheck, AlertCircle, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const KIND_LABEL = { assessment: 'Assessment', evidence: 'Evidence' };
const KIND_COLOUR = {
  assessment: 'text-cyan-300 border-cyan-500/40 bg-cyan-500/10',
  evidence:   'text-emerald-300 border-emerald-500/40 bg-emerald-500/10',
};

const AdminCodeMaintenance = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const authHeader = useCallback(() => {
    const t = localStorage.getItem('startech_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const loadIndex = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/refactor-reports`, { headers: authHeader() });
      if (r.status === 403) { navigate('/'); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setReports(d.reports || []);
    } catch (e) {
      setError(`Failed to load reports: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [authHeader, navigate]);

  const loadReport = useCallback(async (filename) => {
    setSelected(filename);
    setContent('');
    setError('');
    try {
      const r = await fetch(`${API}/api/admin/refactor-reports/${filename}`, { headers: authHeader() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setContent(await r.text());
    } catch (e) {
      setError(`Failed to load ${filename}: ${e.message}`);
    }
  }, [authHeader]);

  useEffect(() => { loadIndex(); }, [loadIndex]);

  // Group reports by phase for the left rail.
  const grouped = reports.reduce((acc, r) => {
    (acc[r.phase] = acc[r.phase] || []).push(r);
    return acc;
  }, {});
  const phases = Object.keys(grouped).sort();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-code-maintenance">
      {/* Header */}
      <header className="border-b border-cyan-500/20 bg-slate-900/70 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="cm-back-btn"
              onClick={() => navigate('/admin/dashboard')}
              className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-cyan-300 transition-colors"
              title="Back to admin dashboard"
            >
              <ArrowLeft size={16} />
            </button>
            <ShieldCheck size={22} className="text-cyan-400" />
            <div>
              <h1 className="text-lg font-semibold tracking-tight" data-testid="cm-title">Code Maintenance</h1>
              <p className="text-xs text-slate-400">Refactor governance · assessments &amp; evidence reports · admin-only</p>
            </div>
          </div>
          <button
            data-testid="cm-refresh-btn"
            onClick={loadIndex}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-cyan-500/30 hover:border-cyan-400 hover:bg-cyan-500/10 text-cyan-300 transition-colors"
            title="Reload the report index"
          >
            <RefreshCcw size={12} /> Refresh
          </button>
        </div>
      </header>

      {/* Body: two-pane layout */}
      <main className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-12 gap-6">
        {/* Left rail — report index */}
        <aside className="col-span-4 lg:col-span-3">
          <div className="border border-slate-800 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-900/60 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-800">
              Reports · {reports.length}
            </div>
            <div className="divide-y divide-slate-800/70" data-testid="cm-report-list">
              {loading && (
                <div className="p-4 text-xs text-slate-500">Loading…</div>
              )}
              {!loading && reports.length === 0 && (
                <div className="p-4 text-xs text-slate-500 flex items-start gap-2">
                  <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-amber-400" />
                  <span>No reports yet. Assessment + evidence reports appear here as each refactor phase is worked.</span>
                </div>
              )}
              {phases.map((phase) => (
                <div key={phase}>
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-900/30">
                    Phase {phase}
                  </div>
                  {grouped[phase].map((r) => {
                    const active = selected === r.filename;
                    return (
                      <button
                        key={r.filename}
                        data-testid={`cm-report-${r.phase}-${r.kind}`}
                        onClick={() => loadReport(r.filename)}
                        className={
                          'w-full text-left px-3 py-2 hover:bg-slate-800/50 transition-colors ' +
                          (active ? 'bg-slate-800/70 border-l-2 border-cyan-400' : 'border-l-2 border-transparent')
                        }
                      >
                        <div className="flex items-center gap-2">
                          <FileText size={12} className="text-slate-500 flex-shrink-0" />
                          <span className={
                            'inline-block text-[10px] px-1.5 py-0.5 rounded border ' +
                            (KIND_COLOUR[r.kind] || 'text-slate-300 border-slate-600')
                          }>
                            {KIND_LABEL[r.kind] || r.kind}
                          </span>
                        </div>
                        <div className="text-[11px] mt-1 text-slate-400 truncate">{r.filename}</div>
                        <div className="text-[10px] text-slate-600 mt-0.5">
                          {(r.size_bytes / 1024).toFixed(1)} KB · {new Date(r.modified_at).toLocaleString()}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Right pane — report content */}
        <section className="col-span-8 lg:col-span-9">
          {error && (
            <div className="mb-4 p-3 border border-red-500/40 bg-red-500/10 rounded text-sm text-red-300 flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" /> {error}
            </div>
          )}
          {!selected && !error && (
            <div className="border border-slate-800 rounded-lg p-8 text-center text-slate-500 text-sm">
              Select a report from the left rail to view its contents.
            </div>
          )}
          {selected && (
            <div className="border border-slate-800 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-slate-900/60 text-xs text-slate-400 border-b border-slate-800 flex items-center justify-between">
                <span data-testid="cm-selected-filename">{selected}</span>
                <span className="text-slate-600">{content.length.toLocaleString()} chars</span>
              </div>
              <pre
                data-testid="cm-report-content"
                className="p-4 text-[12px] leading-relaxed text-slate-200 whitespace-pre-wrap break-words font-mono max-h-[75vh] overflow-y-auto"
              >{content}</pre>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminCodeMaintenance;
